import type {
	API_ArchiveScheduleRequest,
	API_ArchiveScheduleWithResolutionsRequest,
	API_CreateScheduleRequest,
	API_CreateScheduleResponse,
	API_ListOrganizationSchedulesPaginatedResponse,
	API_ListOrganizationSchedulesResponse,
	API_ListScheduleSessionsWithRelationsResponse,
	API_MigrateSchedulesRequest,
	API_MigrateSchedulesResponse,
	API_RegenerateSessionsResponse,
	API_ScheduleSessionWithRelationsResponse,
	API_UpdateScheduleRequest,
	API_UpdateScheduleResponse,
	CalendarDate,
	DayOfWeek,
	FE_ScheduleSession,
	PaginationRequest,
	Schedule,
	ScheduleType,
} from "@pilatestime/shared-types";
import { todayIn } from "@pilatestime/shared-types";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import { convertScheduleSessionToEventSession } from "@/utils/attendanceUtils";
import { translateBackendError } from "@/utils/errorTranslation";
import api from "./api_client";

// API functions
export const fetchOrganizationSchedules = (
	token: string,
	organizationId: string,
): Promise<API_ListOrganizationSchedulesResponse> =>
	api
		.get(`/organizations/${organizationId}/schedules`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

// Filter params type for schedule list
export interface ScheduleFilterParams {
	staffMemberId?: string;
	type?: ScheduleType;
	isActive?: boolean;
	// "Enrolling" lifecycle filter: active schedules with zero enrollments.
	enrolling?: boolean;
	daysOfWeek?: DayOfWeek[];
}

// Paginated schedule list
export const fetchOrganizationSchedulesPaginated = (
	token: string,
	organizationId: string,
	paginationParams: PaginationRequest,
	searchTerm?: string,
	filterParams?: ScheduleFilterParams,
): Promise<API_ListOrganizationSchedulesPaginatedResponse> =>
	api
		.get(`/organizations/${organizationId}/schedules/paginated`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				...paginationParams,
				...(searchTerm && { search: searchTerm }),
				...(filterParams?.staffMemberId && {
					staffMemberId: filterParams.staffMemberId,
				}),
				...(filterParams?.type && { type: filterParams.type }),
				...(filterParams?.isActive !== undefined && {
					isActive: filterParams.isActive,
				}),
				...(filterParams?.enrolling && { enrolling: true }),
				...(filterParams?.daysOfWeek &&
					filterParams.daysOfWeek.length > 0 && {
						daysOfWeek: filterParams.daysOfWeek.join(","),
					}),
			},
		})
		.then((res) => res.data);

export const fetchScheduleById = (
	token: string,
	organizationId: string,
	scheduleId: string,
): Promise<Schedule> =>
	api
		.get(`/organizations/${organizationId}/schedules/${scheduleId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const fetchSchedulesByStaffMember = (
	token: string,
	organizationId: string,
	staffId: string,
	options?: { hasActiveFutureSessions?: boolean },
): Promise<API_ListOrganizationSchedulesResponse> =>
	api
		.get(`/organizations/${organizationId}/staff/${staffId}/schedules`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params: {
				...(options?.hasActiveFutureSessions && {
					hasActiveFutureSessions: "true",
				}),
			},
		})
		.then((res) => res.data);

export const createSchedule = (
	token: string,
	organizationId: string,
	data: API_CreateScheduleRequest,
): Promise<API_CreateScheduleResponse> =>
	api
		.post(`/organizations/${organizationId}/schedules`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const updateSchedule = (
	token: string,
	organizationId: string,
	scheduleId: string,
	data: API_UpdateScheduleRequest,
): Promise<API_UpdateScheduleResponse> =>
	api
		.put(`/organizations/${organizationId}/schedules/${scheduleId}`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const deleteSchedule = (
	token: string,
	organizationId: string,
	scheduleId: string,
): Promise<void> =>
	api
		.delete(`/organizations/${organizationId}/schedules/${scheduleId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const archiveSchedule = (
	token: string,
	organizationId: string,
	scheduleId: string,
	// Accepts the optional per-enrollment `resolutions` (D8). The backend applies
	// them and the archive atomically; an empty/absent array keeps the no-blocker
	// path unchanged.
	data: API_ArchiveScheduleRequest &
		Partial<Pick<API_ArchiveScheduleWithResolutionsRequest, "resolutions">>,
): Promise<void> =>
	api
		.post(
			`/organizations/${organizationId}/schedules/${scheduleId}/archive`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const unarchiveSchedule = (
	token: string,
	organizationId: string,
	scheduleId: string,
): Promise<void> =>
	api
		.post(
			`/organizations/${organizationId}/schedules/${scheduleId}/unarchive`,
			{},
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const regenerateSessions = (
	token: string,
	organizationId: string,
	scheduleId: string,
): Promise<API_RegenerateSessionsResponse> =>
	api
		.post(
			`/organizations/${organizationId}/schedules/${scheduleId}/sessions/regenerate`,
			{},
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

// Query options
export const fetchOrganizationSchedulesQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["organization_schedules", organizationId, userId],
		queryFn: () => fetchOrganizationSchedules(token, organizationId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchOrganizationSchedulesPaginatedQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	paginationParams: PaginationRequest,
	searchTerm?: string,
	filterParams?: ScheduleFilterParams,
) =>
	queryOptions({
		queryKey: [
			"organization_schedules_paginated",
			organizationId,
			userId,
			paginationParams,
			searchTerm ?? "",
			filterParams?.staffMemberId ?? "",
			filterParams?.type ?? "",
			filterParams?.isActive ?? "",
			filterParams?.enrolling ?? "",
			filterParams?.daysOfWeek ?? [],
		],
		queryFn: () =>
			fetchOrganizationSchedulesPaginated(
				token,
				organizationId,
				paginationParams,
				searchTerm,
				filterParams,
			),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchScheduleByIdQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	scheduleId: string,
) =>
	queryOptions({
		queryKey: ["schedule", organizationId, scheduleId, userId],
		queryFn: () => fetchScheduleById(token, organizationId, scheduleId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchSchedulesByStaffMemberQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	staffId: string,
	options?: { hasActiveFutureSessions?: boolean },
) =>
	queryOptions({
		queryKey: [
			"staff_schedules",
			organizationId,
			staffId,
			userId,
			options?.hasActiveFutureSessions ?? false,
		],
		queryFn: () =>
			fetchSchedulesByStaffMember(token, organizationId, staffId, options),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

// Mutation hooks
export const useCreateSchedule = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			data,
		}: {
			token: string;
			organizationId: string;
			data: API_CreateScheduleRequest;
		}) => createSchedule(token, organizationId, data),
		toastMessages: {
			loading: t("schedules.toasts.creating"),
			success: t("schedules.toasts.createSuccess"),
			error: t("schedules.toasts.createError"),
		},
		showErrorToast: false, // Disable automatic error toast to handle manually
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization schedules
			queryClient.invalidateQueries({
				queryKey: ["organization_schedules", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_schedules_paginated",
					variables.organizationId,
				],
			});
			// Invalidate staff schedules (for staff detail page schedules tab)
			queryClient.invalidateQueries({
				queryKey: ["staff_schedules", variables.organizationId],
			});
		},
		onError: (error: AxiosError) => {
			// Handle specific error cases
			const errorData = error.response?.data as { message?: string };
			const specificMessage = errorData?.message;

			if (specificMessage) {
				// Translate the backend error message to the user's language
				const translatedMessage = translateBackendError(specificMessage, t);
				toast.error(translatedMessage);
			} else {
				// For all other errors, show the generic error message
				toast.error(t("schedules.toasts.createError"));
			}
		},
	});
};

export const useUpdateSchedule = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			scheduleId,
			data,
		}: {
			token: string;
			organizationId: string;
			scheduleId: string;
			data: API_UpdateScheduleRequest;
		}) => updateSchedule(token, organizationId, scheduleId, data),
		toastMessages: {
			loading: t("schedules.toasts.updating"),
			success: t("schedules.toasts.updateSuccess"),
			error: t("schedules.toasts.updateError"),
		},
		showErrorToast: false, // Disable automatic error toast to handle manually
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization schedules
			queryClient.invalidateQueries({
				queryKey: ["organization_schedules", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_schedules_paginated",
					variables.organizationId,
				],
			});
			// Invalidate specific schedule query
			queryClient.invalidateQueries({
				queryKey: ["schedule", variables.organizationId, variables.scheduleId],
			});
			// Invalidate staff schedules (for staff detail page schedules tab)
			queryClient.invalidateQueries({
				queryKey: ["staff_schedules", variables.organizationId],
			});
			// Invalidate calendar/attendance queries to refresh schedule sessions
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "list", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "monthly", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["attendance", "list"],
			});
		},
		onError: (error: AxiosError) => {
			// Handle specific error cases
			const errorData = error.response?.data as { message?: string };
			const specificMessage = errorData?.message;

			if (specificMessage) {
				// Translate the backend error message to the user's language
				const translatedMessage = translateBackendError(specificMessage, t);
				toast.error(translatedMessage);
			} else {
				// For all other errors, show the generic error message
				toast.error(t("schedules.toasts.updateError"));
			}
		},
	});
};

export const useDeleteSchedule = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			scheduleId,
		}: {
			token: string;
			organizationId: string;
			scheduleId: string;
		}) => deleteSchedule(token, organizationId, scheduleId),
		toastMessages: {
			loading: t("schedules.toasts.deleting"),
			success: t("schedules.toasts.deleteSuccess"),
			error: t("schedules.toasts.deleteError"),
		},
		// Handle the 409 deletion-conflict payload at the call site (typed
		// `API_DeleteScheduleConflictResponse`) instead of showing a generic toast.
		showErrorToast: false,
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization schedules
			queryClient.invalidateQueries({
				queryKey: ["organization_schedules", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_schedules_paginated",
					variables.organizationId,
				],
			});
			// Invalidate staff schedules (for staff detail page schedules tab)
			queryClient.invalidateQueries({
				queryKey: ["staff_schedules", variables.organizationId],
			});
			// Remove specific schedule from cache
			queryClient.removeQueries({
				queryKey: ["schedule", variables.organizationId, variables.scheduleId],
			});
		},
		onError: (error: AxiosError) => {
			// A 409 carries the typed `API_DeleteScheduleConflictResponse`; the
			// caller inspects it to render the conflict. Only surface a toast for
			// other (unexpected) failures so we never mask the conflict UI.
			if (error.response?.status !== 409) {
				toast.error(t("schedules.toasts.deleteError"));
			}
		},
	});
};

/**
 * Invalidates every query touched by an archive/unarchive: schedule lists,
 * the schedule detail, staff schedules, and the session/attendance caches
 * (archive may cancel future sessions). Shared by both hooks to stay DRY.
 */
const invalidateScheduleLifecycleQueries = (
	queryClient: ReturnType<typeof useQueryClient>,
	organizationId: string,
	scheduleId: string,
) => {
	queryClient.invalidateQueries({
		queryKey: ["organization_schedules", organizationId],
	});
	queryClient.invalidateQueries({
		queryKey: ["organization_schedules_paginated", organizationId],
	});
	queryClient.invalidateQueries({
		queryKey: ["schedule", organizationId, scheduleId],
	});
	queryClient.invalidateQueries({
		queryKey: ["staff_schedules", organizationId],
	});
	queryClient.invalidateQueries({
		queryKey: ["scheduleSessions", "list", organizationId],
	});
	queryClient.invalidateQueries({
		queryKey: ["scheduleSessions", "monthly", organizationId],
	});
	queryClient.invalidateQueries({
		queryKey: ["attendance", "list"],
	});
};

export const useArchiveSchedule = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			scheduleId,
			data,
		}: {
			token: string;
			organizationId: string;
			scheduleId: string;
			data: API_ArchiveScheduleRequest &
				Partial<Pick<API_ArchiveScheduleWithResolutionsRequest, "resolutions">>;
		}) => archiveSchedule(token, organizationId, scheduleId, data),
		toastMessages: {
			loading: t("schedules.toasts.archiving"),
			success: t("schedules.toasts.archiveSuccess"),
			error: t("schedules.toasts.archiveError"),
		},
		// A 409 carries the typed `API_ArchiveScheduleConflictResponse` (per-row
		// resolution conflicts). The archive modal renders those inline, so we
		// suppress the toast for 409 and only toast unexpected failures.
		showErrorToast: false,
		onSuccess: (_data, variables) => {
			invalidateScheduleLifecycleQueries(
				queryClient,
				variables.organizationId,
				variables.scheduleId,
			);
		},
		onError: (error: AxiosError) => {
			if (error.response?.status !== 409) {
				toast.error(t("schedules.toasts.archiveError"));
			}
		},
	});
};

export const useUnarchiveSchedule = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			scheduleId,
		}: {
			token: string;
			organizationId: string;
			scheduleId: string;
		}) => unarchiveSchedule(token, organizationId, scheduleId),
		toastMessages: {
			loading: t("schedules.toasts.unarchiving"),
			success: t("schedules.toasts.unarchiveSuccess"),
			error: t("schedules.toasts.unarchiveError"),
		},
		onSuccess: (_data, variables) => {
			invalidateScheduleLifecycleQueries(
				queryClient,
				variables.organizationId,
				variables.scheduleId,
			);
		},
	});
};

/**
 * Regenerate (repair) the sessions of a schedule from its recurrence config.
 * Idempotent — inserts only the missing sessions and returns how many were
 * `created` / `skipped`. On success we invalidate the schedule detail/list
 * queries (so `futureSessionCount` refreshes) plus every schedule-session query,
 * then toast with the created count. The success toast is handled manually
 * because it must interpolate the returned count.
 */
export const useRegenerateSessions = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			scheduleId,
		}: {
			token: string;
			organizationId: string;
			scheduleId: string;
		}) => regenerateSessions(token, organizationId, scheduleId),
		toastMessages: {
			loading: t("schedules.toasts.regenerating"),
			error: t("schedules.toasts.regenerateError"),
		},
		showSuccessToast: false,
		onSuccess: (data, variables) => {
			// Refresh the schedule detail + lists so futureSessionCount updates.
			queryClient.invalidateQueries({
				queryKey: ["schedule", variables.organizationId, variables.scheduleId],
			});
			queryClient.invalidateQueries({
				queryKey: ["organization_schedules", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_schedules_paginated",
					variables.organizationId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: ["staff_schedules", variables.organizationId],
			});
			// Refresh every schedule-session query (calendar, bySchedule, detail).
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions"],
			});
			queryClient.invalidateQueries({
				queryKey: ["attendance", "list"],
			});
			toast.success(
				t("schedules.toasts.regenerateSuccess", { count: data.created }),
			);
		},
	});
};

// Schedule Migration API functions
export const migrateSchedules = (
	token: string,
	organizationId: string,
	sourceStaffId: string,
	data: API_MigrateSchedulesRequest,
): Promise<API_MigrateSchedulesResponse> =>
	api
		.post(
			`/organizations/${organizationId}/staff/${sourceStaffId}/schedules/migrate`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const useMigrateSchedules = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			sourceStaffId,
			data,
		}: {
			token: string;
			organizationId: string;
			sourceStaffId: string;
			data: API_MigrateSchedulesRequest;
		}) => migrateSchedules(token, organizationId, sourceStaffId, data),
		toastMessages: {
			loading: t("staff.migrateSchedules.toasts.migrating"),
			success: t("staff.migrateSchedules.toasts.migrateSuccess"),
			error: t("staff.migrateSchedules.toasts.migrateError"),
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["staff_schedules", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["organization_schedules", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"organization_schedules_paginated",
					variables.organizationId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "list", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions", "monthly", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["attendance", "list"],
			});
			queryClient.invalidateQueries({
				queryKey: ["schedule", variables.organizationId],
			});
			queryClient.invalidateQueries({
				queryKey: ["organization_staff", variables.organizationId],
			});
		},
	});
};

// Schedule Session API functions
export const fetchScheduleSession = async (
	organizationId: string,
	sessionId: string,
): Promise<FE_ScheduleSession> => {
	// Use the list endpoint with a filter to get the single session with relations and attendance
	const params = new URLSearchParams();
	params.append("sessionId", sessionId);

	const response = await api.get<API_ListScheduleSessionsWithRelationsResponse>(
		`/organizations/${organizationId}/schedule-sessions?${params.toString()}`,
	);

	// Find the specific session (should be the only one returned)
	const session = response.data.sessions.find((s) => s.id === sessionId);
	if (!session) {
		throw new Error(`Session ${sessionId} not found`);
	}

	return convertScheduleSessionToEventSession(session, session.attendanceCount);
};

// Schedule Session Query options
export const fetchScheduleSessionQueryOptions = (
	organizationId: string,
	sessionId: string,
) =>
	queryOptions({
		queryKey: ["scheduleSessions", "detail", organizationId, sessionId],
		queryFn: () => fetchScheduleSession(organizationId, sessionId),
		enabled: !!organizationId && !!sessionId,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

// The "Today's schedule" range is a single calendar day, sent as a day string
// (`YYYY-MM-DD`) on both bounds. The endpoint reads them as `CalendarDate`s and
// windows the day itself (see scheduleSessionRoutes.ts), so no instant — and no
// browser-local `setHours` offset — ever reaches the filter.
// The dashboard only ever needs one day, so the range is a single indexed query
// (no per-row fetches — see plan §8 N+1 gate).
function getTodayRange(today: CalendarDate): {
	startDate: CalendarDate;
	endDate: CalendarDate;
} {
	return { startDate: today, endDate: today };
}

// Fetch today's sessions as the RAW relations response. Unlike
// `fetchScheduleSession` we intentionally do NOT map to `FE_ScheduleSession`
// because the dashboard needs `attendanceCapacity` (Schedule.ts:557), which the
// FullCalendar conversion drops.
export const fetchTodayScheduleSessions = async (
	organizationId: string,
	// "Today" is the browser-local calendar day, computed explicitly from the
	// browser's IANA zone rather than derived implicitly from a `Date`'s local
	// accessors. Which day is "today" stays a browser-local decision until an
	// organization timezone exists (plan P2).
	today: CalendarDate = todayIn(
		Intl.DateTimeFormat().resolvedOptions().timeZone,
	),
): Promise<API_ScheduleSessionWithRelationsResponse[]> => {
	const { startDate, endDate } = getTodayRange(today);
	const params = new URLSearchParams();
	params.append("startDate", startDate);
	params.append("endDate", endDate);

	const response = await api.get<API_ListScheduleSessionsWithRelationsResponse>(
		`/organizations/${organizationId}/schedule-sessions?${params.toString()}`,
	);
	return response.data.sessions;
};

// Query options for today's schedule sessions. Keyed on the calendar day so the
// cache is stable across the page's lifetime but refreshes on a new day.
export const fetchTodayScheduleSessionsQueryOptions = (
	userId: string,
	// Auth is injected by the api_client interceptor; `token` is kept only so the
	// call site matches the other dashboard query-option factories.
	_token: string,
	organizationId: string,
) => {
	const dayKey = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
	return queryOptions({
		queryKey: ["scheduleSessions", "today", organizationId, userId, dayKey],
		queryFn: () => fetchTodayScheduleSessions(organizationId),
		enabled: !!organizationId,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};
